import { useImperativeHandle, useRef, useState } from "react";
import { Portal } from "../../portal";

export interface DragHandlePreviewRef {
	setPosition: (anchorX: number, anchorY: number, focalX?: number, focalY?: number) => void;
}

export interface PreviewViewLayout {
	x: number;
	y: number;
	width: number;
	height: number;
}

/** Props Interface */
interface AnnotationDragHandlePreviewProps {
	ref: React.RefObject<DragHandlePreviewRef | null>;
	imageUrl: string;
	viewLayout: PreviewViewLayout;
}

const MAGNIFICATION = 2.5;
const HANDLE_GAP = 30;

export function AnnotationDragHandlePreview({
	ref,
	imageUrl,
	viewLayout,
}: AnnotationDragHandlePreviewProps) {
	/** State */
	const [size] = useState(() => window.innerWidth * 0.1);

	/** Refs */
	const circleRef = useRef<HTMLDivElement>(null);
	const imageRef = useRef<HTMLImageElement>(null);

	/** Hooks */
	useImperativeHandle(
		ref,
		() => ({
			setPosition: (anchorX, anchorY, focalX = anchorX, focalY = anchorY) => {
				if (circleRef.current) {
					circleRef.current.style.transform = `translate(${anchorX - size / 2}px, ${anchorY - size - HANDLE_GAP}px)`;
				}
				if (imageRef.current) {
					const fx = (focalX - viewLayout.x) * MAGNIFICATION;
					const fy = (focalY - viewLayout.y) * MAGNIFICATION;
					imageRef.current.style.transform = `translate(${size / 2 - fx}px, ${size / 2 - fy}px)`;
				}
			},
		}),
		[viewLayout, size],
	);

	/** Render */
	return (
		<Portal>
			<div
				ref={circleRef}
				className="fixed top-0 left-0 z-[999999] aspect-square rounded-full bg-black pointer-events-none overflow-hidden border border-white"
				style={{ width: size, pointerEvents: "none" }}
			>
				<img
					ref={imageRef}
					src={imageUrl}
					alt=""
					className="absolute pointer-events-none"
					style={{
						width: viewLayout.width * MAGNIFICATION,
						height: viewLayout.height * MAGNIFICATION,
						maxWidth: "none",
					}}
				/>
				<div className="w-4 h-0 border-t border-white absolute left-1/2 top-1/2 -translate-x-1/2 mix-blend-difference" />
				<div className="w-0 h-4 border-r border-white absolute left-1/2 top-1/2 -translate-y-1/2 mix-blend-difference" />
				<div
					className="absolute inset-0"
					style={{ background: "linear-gradient(135deg, white, transparent)" }}
				/>
			</div>
		</Portal>
	);
}
